
import React from 'react';
import { BadgeDollarSign, LineChart, Shield, Sparkles, BrainCircuit, Smartphone, Home, Gem, Users, BanknoteIcon, Diamond, ChartBar } from 'lucide-react';
import useScrollReveal from '@/hooks/useScrollReveal';

const FeatureCard = ({ icon, title, description }: { icon: React.ReactNode, title: string, description: string }) => {
  return (
    <div className="glass-card rounded-xl p-6 hover:border-[#9E7C47]/50 transition-all duration-300 group hover:transform hover:scale-105 hover:shadow-lg hover:shadow-[#9E7C47]/20 stagger-item">
     <div className="flex justify-center mb-5 transform group-hover:scale-125 transition-transform duration-500 ease-out">
     {icon}</div>
      <h3 className="text-xl font-semibold mb-3 group-hover:text-gradient transition-colors">{title}</h3>
      <p className="text-white/70">{description}</p>
    </div>
  );
};

const FeaturesSection = () => {
  const mainTitleRef = useScrollReveal();
  const approachCardsRef = useScrollReveal({ threshold: 0.3 });
  const featureCardsRef = useScrollReveal({ threshold: 0.1, staggerDelay: 100 });
  
  return (
    <section id="features" className="py-20 px-4">
      <div className="container mx-auto">
        <div className="text-center mb-16 reveal" ref={mainTitleRef}>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gradient">Complete Wealth Management</h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            Our AI-powered platform combined with expert human advisors brings sophisticated wealth management across multiple asset classes directly to your fingertips.
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-8 mb-16 reveal" ref={approachCardsRef}>
          <div className="flex-1 glass-card rounded-xl p-6 bg-gradient-to-br from-wealth-dark to-[#9E7C47]/20 border border-[#9E7C47]/20 hover:shadow-lg hover:shadow-[#9E7C47]/30 transition-all duration-500 hover:border-[#9E7C47]/40">
            <div className="flex items-center mb-4">
              <BrainCircuit className="h-8 w-8 text-[#F9F1A9] mr-3 animate-pulse-glow" />
              <h3 className="text-2xl font-bold text-gradient">AI-First Approach</h3>
            </div>
            <p className="text-white/80 mb-4">
              Our cutting-edge AI technology forms the foundation of our wealth management platform, continuously analyzing markets and optimizing your portfolio.
            </p>
            <ul className="space-y-2">
              <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                <div className="h-1.5 w-1.5 rounded-full bg-[#9E7C47] mt-2 mr-2"></div>
                <span className="text-white/70">24/7 automated portfolio monitoring</span>
              </li>
              <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                <div className="h-1.5 w-1.5 rounded-full bg-[#9E7C47] mt-2 mr-2"></div>
                <span className="text-white/70">Real-time market analysis across asset classes</span>
              </li>
              <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                <div className="h-1.5 w-1.5 rounded-full bg-[#9E7C47] mt-2 mr-2"></div>
                <span className="text-white/70">Predictive insights for better decision-making</span>
              </li>
            </ul>
          </div>
          
          <div className="flex-1 glass-card rounded-xl p-6 bg-gradient-to-br from-wealth-dark to-[#F9F1A9]/20 border border-[#F9F1A9]/20 hover:shadow-lg hover:shadow-[#F9F1A9]/30 transition-all duration-500 hover:border-[#F9F1A9]/40">
            <div className="flex items-center mb-4">
              <Users className="h-8 w-8 text-[#F9F1A9] mr-3 animate-pulse-glow" />
              <h3 className="text-2xl font-bold">Human Expertise</h3>
            </div>
            <p className="text-white/80 mb-4">
              Complement our AI with access to dedicated wealth managers who provide personalized guidance and expertise when you need it most.
            </p>
            <ul className="space-y-2">
              <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                <div className="h-1.5 w-1.5 rounded-full bg-[#F9F1A9] mt-2 mr-2"></div>
                <span className="text-white/70">Scheduled consultations with certified advisors</span>
              </li>
              <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                <div className="h-1.5 w-1.5 rounded-full bg-[#F9F1A9] mt-2 mr-2"></div>
                <span className="text-white/70">Strategic planning for complex financial decisions</span>
              </li>
              <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                <div className="h-1.5 w-1.5 rounded-full bg-[#F9F1A9] mt-2 mr-2"></div>
                <span className="text-white/70">Personalized wealth management roadmaps</span>
              </li>
            </ul>
          </div>
        </div>
        
        <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center reveal" ref={useScrollReveal()}>Diversified Asset Management</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" ref={featureCardsRef}>
          <FeatureCard 
            icon={<ChartBar className="h-10 w-10 text-[#9E7C47]" />}
            title="Stocks & ETFs"
            description="Intelligent selection and management of equity investments tailored to your risk profile and financial goals."
          />
          
          <FeatureCard 
            icon={<Home className="h-10 w-10 text-[#F9F1A9]" />}
            title="Real Estate"
            description="Strategic property investments across residential, commercial, and REITs with market-specific insights."
          />
          
          <FeatureCard 
            icon={<Gem className="h-10 w-10 text-yellow-500" />}
            title="Gold & Precious Metals"
            description="Optimize allocation to physical gold, digital gold, and precious metal ETFs as a hedge against inflation."
          />
          
          <FeatureCard 
            icon={<BanknoteIcon className="h-10 w-10 text-wealth-light" />}
            title="Bonds & Fixed Income"
            description="Secure your portfolio with strategic bond investments offering stable returns and reduced volatility."
          />
          
          <FeatureCard 
            icon={<Diamond className="h-10 w-10 text-purple-500" />}
            title="Alternative Assets"
            description="Access to carefully vetted alternative investments including private equity, commodities, and collectibles."
          />
          
          <FeatureCard 
            icon={<Smartphone className="h-10 w-10 text-wealth-skyblue" />}
            title="All-In-One Platform"
            description="Manage your entire financial universe from a single dashboard with powerful analytics and insights."
          />
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
